import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { InfoIcon } from 'lucide-react'
import { Button } from 'ui/button'
import { Counter } from 'ui/counter'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from 'ui/drawer'
import { Label } from 'ui/label'
import { toast } from 'ui/sonner'
import { Switch } from 'ui/switch'
import { z } from 'zod/mini'

import { ScopeSnoozeToFrontLanguageControl } from '~/boundedContext/settings/components/scope-snooze-to-front-language-control'
import { useSettingsStore } from '~/stores/settings'

export const SnoozeSettings = () => {
  const smartSnoozeSwitchId = useId()
  const { t } = useTranslation()
  const patchSettings = useSettingsStore(state => state.patchSettings)
  const snoozeDelayDays = useSettingsStore(state => state.snoozeDelayDays)
  const smartSnoozeEnabled = useSettingsStore(state => state.smartSnoozeEnabled)

  const setSnoozeDelayDays = (nextValue: unknown) => {
    const parsedValue = z.int().check(z.minimum(1)).safeParse(nextValue)
    if (!parsedValue.success) {
      toast.error(t('Invalid snooze delay days'))
      console.warn({ message: 'Invalid snooze delay days', nextValue })
      return
    }

    patchSettings({ snoozeDelayDays: parsedValue.data })
  }

  return (
    <section className="flex w-full flex-col gap-4 rounded-xl border border-border/60 bg-card/50 p-3">
      <div className="flex w-full flex-wrap items-center gap-3">
        {/* TODO: should be a label */}
        <p className="min-w-0 grow basis-32 text-sm text-muted-foreground">{t('Snooze delay days')}</p>
        <Counter
          className="min-w-0 grow basis-48 sm:max-w-xs"
          decreaseLabel={t('Decrease snooze delay')}
          increaseLabel={t('Increase snooze delay')}
          inputLabel={t('Snooze delay days input')}
          onValueChange={setSnoozeDelayDays}
          value={snoozeDelayDays}
        />
      </div>
      <div className="flex w-full flex-wrap items-center gap-2 border-t border-border/40 pt-4">
        <Switch
          checked={smartSnoozeEnabled}
          id={smartSnoozeSwitchId}
          onCheckedChange={isChecked => patchSettings({ smartSnoozeEnabled: isChecked })}
        />
        <Label className="min-w-0" htmlFor={smartSnoozeSwitchId}>
          {t('Smart snooze')}
        </Label>
        <Drawer>
          <DrawerTrigger
            render={<Button aria-label={t('Smart snooze info')} className="shrink-0" size="icon" variant="ghost" />}
          >
            <InfoIcon className="size-4 stroke-primary" />
          </DrawerTrigger>
          <DrawerContent>
            <div className="mx-auto w-full max-w-sm">
              <DrawerHeader>
                <DrawerTitle>{t('Smart snooze')}</DrawerTitle>
                <DrawerDescription>{t('Snooze delay doubles each time')}</DrawerDescription>
              </DrawerHeader>
              <DrawerFooter className="p-4">
                <DrawerClose render={<Button variant="outline" />}>{t('Close')}</DrawerClose>
              </DrawerFooter>
            </div>
          </DrawerContent>
        </Drawer>
      </div>
      <ScopeSnoozeToFrontLanguageControl className="border-t border-border/40 pt-4" />
    </section>
  )
}
